/**
 * DevicePermissionsService — façade unifiée pour les permissions natives
 * du device exposées par le navigateur dans une PWA installée.
 *
 * Couvre :
 *   - 🔔 Notifications (Permission API)
 *   - 📍 Geolocation (Permission API + getCurrentPosition)
 *   - 🔆 Wake Lock (Screen Wake Lock API) — utile pour le POS
 *   - 💾 Persistent Storage (Storage API)
 *   - 🔄 Background Sync (SyncManager)
 *   - ⏰ Periodic Background Sync (PeriodicSyncManager)
 *   - 🛜 Network Information (online + effectiveType)
 *
 * Chaque permission expose :
 *   - ``supported``    : feature détectée dans le browser
 *   - ``state``        : 'granted' | 'denied' | 'prompt' | 'unknown' | 'unsupported'
 *   - ``request()``    : déclenche le prompt natif (la PWA installée apparaît
 *                        alors dans les Réglages > Apps du device)
 *
 * Note : iOS Safari restreint plusieurs APIs (pas de Background Sync, pas
 * de Periodic Sync, Web Push limité). Le service s'adapte gracieusement
 * en retournant ``unsupported`` plutôt que de lancer une erreur.
 */
import { Injectable, signal } from '@angular/core';

type PermState = 'granted' | 'denied' | 'prompt' | 'unknown' | 'unsupported';

@Injectable({ providedIn: 'root' })
export class DevicePermissionsService {

  // ────────────────────── signals d'état ──────────────────────
  readonly notifications = signal<PermState>('unknown');
  readonly geolocation = signal<PermState>('unknown');
  readonly wakeLock = signal<PermState>('unknown');
  readonly persistentStorage = signal<PermState>('unknown');
  readonly backgroundSync = signal<PermState>('unknown');
  readonly periodicSync = signal<PermState>('unknown');
  readonly online = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  readonly effectiveType = signal<string | null>(null);

  private wakeLockSentinel: any = null;

  async refreshAll(): Promise<void> {
    await Promise.allSettled([
      this.refreshNotifications(),
      this.refreshGeolocation(),
      this.refreshWakeLock(),
      this.refreshPersistentStorage(),
      this.refreshBackgroundSync(),
      this.refreshPeriodicSync(),
    ]);
    this.online.set(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const ct = (navigator as any).connection;
    if (ct?.effectiveType) this.effectiveType.set(ct.effectiveType);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.online.set(true));
      window.addEventListener('offline', () => this.online.set(false));
    }
  }

  // ────────────────────── Notifications ──────────────────────
  async refreshNotifications(): Promise<void> {
    if (typeof Notification === 'undefined') {
      this.notifications.set('unsupported');
      return;
    }
    this.notifications.set(this.normalize(Notification.permission));
  }

  async requestNotifications(): Promise<PermState> {
    if (typeof Notification === 'undefined') return 'unsupported';
    const p = await Notification.requestPermission();
    const s = this.normalize(p);
    this.notifications.set(s);
    return s;
  }

  // ────────────────────── Geolocation ──────────────────────
  async refreshGeolocation(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.geolocation.set('unsupported');
      return;
    }
    try {
      const p = await (navigator as any).permissions.query({ name: 'geolocation' });
      this.geolocation.set(this.normalize(p.state));
      p.onchange = () => this.geolocation.set(this.normalize(p.state));
    } catch {
      this.geolocation.set('prompt');
    }
  }

  /** Déclenche le prompt natif Geolocation. */
  requestGeolocation(opts?: PositionOptions): Promise<{ ok: boolean; lat?: number; lng?: number; accuracy?: number; error?: string }> {
    return new Promise((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        resolve({ ok: false, error: 'Geolocation non supportée.' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.geolocation.set('granted');
          resolve({ ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) this.geolocation.set('denied');
          resolve({ ok: false, error: err.message });
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000, ...opts },
      );
    });
  }

  // ────────────────────── Wake Lock ──────────────────────
  async refreshWakeLock(): Promise<void> {
    if (typeof (navigator as any).wakeLock?.request !== 'function') {
      this.wakeLock.set('unsupported');
      return;
    }
    this.wakeLock.set(this.wakeLockSentinel ? 'granted' : 'prompt');
  }

  async acquireWakeLock(): Promise<{ ok: boolean; error?: string }> {
    try {
      if (typeof (navigator as any).wakeLock?.request !== 'function') {
        return { ok: false, error: 'Wake Lock non supporté sur ce device.' };
      }
      this.wakeLockSentinel = await (navigator as any).wakeLock.request('screen');
      this.wakeLockSentinel.addEventListener('release', () => {
        this.wakeLockSentinel = null;
        this.wakeLock.set('prompt');
      });
      this.wakeLock.set('granted');
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || String(err) };
    }
  }

  releaseWakeLock(): void {
    try { this.wakeLockSentinel?.release?.(); } catch {/* */}
    this.wakeLockSentinel = null;
    this.wakeLock.set('prompt');
  }

  // ────────────────────── Persistent Storage ──────────────────────
  async refreshPersistentStorage(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.storage?.persisted) {
      this.persistentStorage.set('unsupported');
      return;
    }
    try {
      const yes = await navigator.storage.persisted();
      this.persistentStorage.set(yes ? 'granted' : 'prompt');
    } catch {
      this.persistentStorage.set('unknown');
    }
  }

  async requestPersistentStorage(): Promise<PermState> {
    if (!navigator?.storage?.persist) return 'unsupported';
    try {
      const ok = await navigator.storage.persist();
      const s: PermState = ok ? 'granted' : 'denied';
      this.persistentStorage.set(s);
      return s;
    } catch {
      return 'denied';
    }
  }

  async storageEstimate(): Promise<{ usage?: number; quota?: number } | null> {
    if (!navigator?.storage?.estimate) return null;
    try { return await navigator.storage.estimate(); } catch { return null; }
  }

  // ────────────────────── Background Sync ──────────────────────
  async refreshBackgroundSync(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      this.backgroundSync.set('unsupported');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      this.backgroundSync.set(reg && 'sync' in reg ? 'granted' : 'unsupported');
    } catch {
      this.backgroundSync.set('unsupported');
    }
  }

  async registerBackgroundSync(tag = 'vd-order-replay'): Promise<{ ok: boolean; error?: string }> {
    try {
      const reg: any = await navigator.serviceWorker.ready;
      if (!reg || !('sync' in reg)) return { ok: false, error: 'Background Sync non supporté.' };
      await reg.sync.register(tag);
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || String(err) };
    }
  }

  // ────────────────────── Periodic Sync ──────────────────────
  async refreshPeriodicSync(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      this.periodicSync.set('unsupported');
      return;
    }
    try {
      const p = await (navigator as any).permissions.query({ name: 'periodic-background-sync' });
      this.periodicSync.set(this.normalize(p.state));
    } catch {
      this.periodicSync.set('unsupported');
    }
  }

  async registerPeriodicSync(tag = 'vd-keep-fresh', minIntervalHours = 12): Promise<{ ok: boolean; error?: string }> {
    try {
      const reg: any = await navigator.serviceWorker.ready;
      if (!reg?.periodicSync?.register) return { ok: false, error: 'Periodic Sync non supporté.' };
      await reg.periodicSync.register(tag, { minInterval: minIntervalHours * 3600 * 1000 });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || String(err) };
    }
  }

  // ────────────────────── helpers ──────────────────────
  private normalize(s: string): PermState {
    if (s === 'granted' || s === 'denied' || s === 'prompt') return s;
    if (s === 'default') return 'prompt';
    return 'unknown';
  }
}
